import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PanelLeftOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AppsOutlinedIcon from "@mui/icons-material/AppsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import RoomServiceOutlinedIcon from "@mui/icons-material/RoomServiceOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import RestaurantOutlinedIcon from "@mui/icons-material/RestaurantOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import { fetchMenu, findNode } from "@coremind/menu-core";
import { useWorkspaces } from "@coremind/workspaces-core";
import { listMySiteMenu, SITE_MENU_CHANGED_EVENT } from "../useSiteMenu";
import { siteCardHostname } from "../verticals";
const ICONS_BY_NAME = {
    home: _jsx(HomeOutlinedIcon, { sx: { fontSize: 18 } }),
    description: _jsx(DescriptionOutlinedIcon, { sx: { fontSize: 18 } }),
    settings: _jsx(SettingsOutlinedIcon, { sx: { fontSize: 18 } }),
    person: _jsx(PersonOutlineIcon, { sx: { fontSize: 18 } }),
    inventory: _jsx(Inventory2OutlinedIcon, { sx: { fontSize: 18 } }),
    dashboard: _jsx(DashboardOutlinedIcon, { sx: { fontSize: 18 } }),
    task: _jsx(TaskAltOutlinedIcon, { sx: { fontSize: 18 } }),
    service: _jsx(RoomServiceOutlinedIcon, { sx: { fontSize: 18 } }),
    list: _jsx(ListAltOutlinedIcon, { sx: { fontSize: 18 } }),
    event: _jsx(EventAvailableOutlinedIcon, { sx: { fontSize: 18 } }),
    calendar: _jsx(CalendarMonthOutlinedIcon, { sx: { fontSize: 18 } }),
    recipes: _jsx(RestaurantOutlinedIcon, { sx: { fontSize: 18 } }),
    chorystore: _jsx(Diversity3OutlinedIcon, { sx: { fontSize: 18 } }),
};
function iconFor(iconName) {
    return (iconName && ICONS_BY_NAME[iconName]) || _jsx(AppsOutlinedIcon, { sx: { fontSize: 18 } });
}
// Mock only -- see module comment. Shape (headers/parents/children/badges) is real, content is
// this site's own product direction (Home Inventory & Services, per the Tend mockup), not
// MUI-docs placeholder text.
const MOCK_GROUPS = [
    {
        section: null,
        nodes: [
            { id: "overview", label: "Overview", path: "/app", icon: iconFor("home") },
            { id: "app-store", label: "App Store", path: "/app/store", icon: iconFor("apps") },
            { id: "proposals", label: "Proposals", path: "/app/proposals", icon: iconFor("description"), badge: { label: "3", tone: "neutral" } },
        ],
    },
    {
        section: "Life admin",
        nodes: [
            {
                id: "inventory",
                label: "Home Inventory",
                icon: iconFor("inventory"),
                children: [
                    { id: "inventory-overview", label: "Owned Items", path: "/app/inventory", icon: iconFor("dashboard") },
                    { id: "inventory-tasks", label: "Maintenance Tasks", path: "/app/inventory/tasks", icon: iconFor("task"), badge: { label: "NEW", tone: "accent" } },
                ],
            },
            {
                id: "services",
                label: "Services",
                icon: iconFor("service"),
                children: [
                    { kind: "header", id: "services-h", label: "Booking" },
                    { id: "services-catalog", label: "Catalog", path: "/app/services", icon: iconFor("list") },
                    { id: "services-appts", label: "Appointments", path: "/app/services/appointments", icon: iconFor("event"), badge: { label: "2", tone: "neutral" } },
                ],
            },
            // Real, federated calendar-ui. The old static site-library-marketplace mockup at
            // "/app/calendar" (previously a separate "Calendar Hub" entry) was removed -- this is now
            // the only Calendar menu item.
            { id: "calendar-hub", label: "Calendar", path: "/app/calendar-hub", icon: iconFor("calendar") },
            { id: "recipes", label: "Recipes", path: "/app/recipes", icon: iconFor("recipes") },
            { id: "chorystore", label: "Chorystore", path: "/app/chorystore", icon: iconFor("chorystore") },
        ],
    },
    {
        section: "Workspace",
        nodes: [{ id: "settings", label: "Settings", path: "/app/settings", icon: iconFor("settings") }],
    },
];
function mapNode(node) {
    const children = (node.children ?? [])
        .filter((c) => !!c.route_path || (c.children ?? []).length > 0)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(mapNode);
    return {
        id: node.component_key ?? node.menu_item_id,
        label: node.label,
        path: node.route_path ?? undefined,
        icon: iconFor(node.icon_name),
        children: children.length > 0 ? children : undefined,
    };
}
// Groups the top-level nodes into header-separated bands using menu-core's own `menu_section`
// field -- consecutive nodes sharing a section stay together under one header; nodes with no
// section render with no header above them, same as today's flat fallback. Real menu-core data
// never nests a header inside a node's own children (no backend field for that yet), so this
// only ever produces top-level NavNode entries -- MOCK_GROUPS is the only source of nested
// headers today.
function groupBySection(tree) {
    const groups = [];
    for (const node of tree) {
        const section = node.menu_section ?? null;
        const last = groups[groups.length - 1];
        if (last && last.section === section)
            last.nodes.push(mapNode(node));
        else
            groups.push({ section, nodes: [mapNode(node)] });
    }
    return groups;
}
const WWW_COMPONENT_KEY = "www";
// Real, per-user site-menu section (see useSiteMenu.ts / build order step 3 of
// 01-www-site-library-and-dashboard-foundation.md) -- deliberately NOT routed through
// fetchMenu()/component_key="www" like the rest of this function. That tree is driven by the
// module-level app-entitlement system (MenuItem/WorkspaceAppEntitlement), a different
// permission axis (features within an already-added site) from "did this user pin whole-site X
// to their own nav." Reusing it here would conflate the two, so this fetches
// GET /tenants/{tenant_id}/site-menu directly and renders it as its own section, alongside
// (not replacing) the mock "Life admin" preview content below, which isn't tied to real sites at
// all and has nothing to do with the site-library mechanism this section surfaces.
function useMySitesGroup() {
    const { selectedWorkspaceId } = useWorkspaces();
    const [nodes, setNodes] = useState(null);
    useEffect(() => {
        if (!selectedWorkspaceId) {
            setNodes(null);
            return;
        }
        let cancelled = false;
        const load = () => {
            listMySiteMenu(selectedWorkspaceId)
                .then((items) => {
                if (cancelled)
                    return;
                setNodes(items.map((item) => ({
                    id: `site-menu-${item.site_id}`,
                    label: item.label,
                    path: `https://${siteCardHostname(item.slug)}`,
                    external: true,
                    icon: iconFor(item.slug),
                })));
            })
                .catch(() => {
                if (!cancelled)
                    setNodes(null);
            });
        };
        load();
        // AppStorePage's Add/Remove buttons live inside this same layout but are a separate
        // component instance -- this event (dispatched by useSiteMenu.ts on every successful
        // add/remove) is what makes this section update live instead of only on next mount/workspace
        // switch.
        window.addEventListener(SITE_MENU_CHANGED_EVENT, load);
        return () => {
            cancelled = true;
            window.removeEventListener(SITE_MENU_CHANGED_EVENT, load);
        };
    }, [selectedWorkspaceId]);
    if (!nodes || nodes.length === 0)
        return null;
    return { section: "My Sites", nodes };
}
function useNavGroups() {
    const [groups, setGroups] = useState(MOCK_GROUPS);
    const mySitesGroup = useMySitesGroup();
    useEffect(() => {
        let cancelled = false;
        fetchMenu()
            .then((tree) => {
            if (cancelled)
                return;
            const wwwNode = findNode(tree, WWW_COMPONENT_KEY);
            const children = wwwNode?.children ?? [];
            if (children.length === 0)
                return; // keep the mock
            const mapped = groupBySection([...children].sort((a, b) => a.sort_order - b.sort_order));
            if (mapped.some((g) => g.nodes.length > 0))
                setGroups(mapped);
        })
            .catch(() => {
            // keep the mock -- same silent-fallback convention as orgs/src/Layout.tsx
        });
        return () => {
            cancelled = true;
        };
    }, []);
    if (!mySitesGroup)
        return groups;
    return [groups[0], mySitesGroup, ...groups.slice(1)];
}
function flattenPaths(entries) {
    return entries.flatMap((e) => (e.kind === "header" ? [] : [...(e.path ? [e.path] : []), ...(e.children ? flattenPaths(e.children) : [])]));
}
// Longest-matching-path wins across the whole tree, not "does this path prefix-match" per row in
// isolation -- otherwise a short path like Home's "/app" would show as active on "/app/proposals"
// too, since that also satisfies "/app"'s own startsWith("/app/") check.
function useBestActivePath(groups, pathname) {
    return useMemo(() => {
        const allPaths = groups.flatMap((g) => flattenPaths(g.nodes));
        let best;
        for (const p of allPaths) {
            if ((pathname === p || pathname.startsWith(`${p}/`)) && (!best || p.length > best.length))
                best = p;
        }
        return best;
    }, [groups, pathname]);
}
function findAncestorIds(entries, activePath) {
    if (!activePath)
        return null;
    for (const entry of entries) {
        if (entry.kind === "header")
            continue;
        if (entry.path === activePath)
            return [];
        if (entry.children) {
            const sub = findAncestorIds(entry.children, activePath);
            if (sub)
                return [entry.id, ...sub];
        }
    }
    return null;
}
const BADGE_COLORS = {
    neutral: { bg: "action.selected", fg: "text.secondary" },
    accent: { bg: "rgba(99, 102, 241, 0.16)", fg: "#a5b4fc" },
    success: { bg: "rgba(52, 211, 153, 0.14)", fg: "#6ee7b7" },
    warning: { bg: "rgba(251, 191, 36, 0.14)", fg: "#fcd34d" },
    danger: { bg: "rgba(248, 113, 113, 0.14)", fg: "#fca5a5" },
};
function NavRow({ entry, depth, collapsed, bestActivePath, expandedIds, onToggle, }) {
    if (entry.kind === "header") {
        if (collapsed)
            return _jsx(Box, { sx: { height: "1px", mx: 0.75, my: 0.75, bgcolor: "divider" } });
        return (_jsx(Typography, { sx: { pl: 1.25 + depth * 1.5, pr: 1, pt: 1, pb: 0.5, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary" }, children: entry.label }));
    }
    const node = entry;
    const hasChildren = !!node.children?.length;
    const isOpen = expandedIds.has(node.id);
    const active = !!node.path && node.path === bestActivePath;
    const content = (_jsxs(Box, { ...(hasChildren
            ? { component: "button", type: "button", onClick: () => onToggle(node.id) }
            : node.external
                ? { component: "a", href: node.path, target: "_blank", rel: "noopener noreferrer" }
                : { component: Link, to: node.path }), sx: {
            all: "unset",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            minHeight: 30,
            py: 0.5,
            pl: collapsed ? 0 : 1.25 + depth * 1.5,
            pr: 1,
            borderRadius: 1,
            cursor: "pointer",
            justifyContent: collapsed ? "center" : "flex-start",
            color: active ? "#a5b4fc" : "text.secondary",
            bgcolor: active ? "rgba(99, 102, 241, 0.14)" : "transparent",
            fontWeight: active ? 600 : 400,
            "&:hover": { bgcolor: active ? "rgba(99, 102, 241, 0.14)" : "action.hover" },
        }, children: [_jsx(Box, { sx: { display: "flex", flexShrink: 0, color: active ? "#a5b4fc" : "text.secondary" }, children: node.icon }), !collapsed && (_jsx(Typography, { component: "span", sx: { fontSize: 13.5, fontWeight: "inherit", color: "inherit", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: node.label })), !collapsed && node.badge && (_jsx(Box, { sx: {
                    flexShrink: 0,
                    fontSize: 10.5,
                    fontWeight: 700,
                    lineHeight: 1.6,
                    px: 0.75,
                    borderRadius: 999,
                    bgcolor: BADGE_COLORS[node.badge.tone].bg,
                    color: BADGE_COLORS[node.badge.tone].fg,
                }, children: node.badge.label })), !collapsed && hasChildren && (_jsx(ChevronRightIcon, { sx: { fontSize: 16, flexShrink: 0, color: "text.secondary", transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 120ms ease" } })), collapsed && node.badge && (_jsx(Box, { sx: { position: "absolute", top: 5, right: 6, width: 6, height: 6, borderRadius: "50%", bgcolor: "#818cf8" } }))] }));
    return (_jsxs(Box, { sx: { position: "relative" }, children: [collapsed ? (_jsx(Tooltip, { title: node.label, placement: "right", children: content })) : (content), hasChildren && !collapsed && isOpen && (_jsx(Box, { children: node.children.map((child) => (_jsx(NavRow, { entry: child, depth: depth + 1, collapsed: collapsed, bestActivePath: bestActivePath, expandedIds: expandedIds, onToggle: onToggle }, child.id))) }))] }));
}
const SIDEBAR_WIDTH = 240;
const SIDEBAR_WIDTH_COLLAPSED = 52;
export default function AppSidebarLayout({ children, defaultCollapsed = false }) {
    const { pathname } = useLocation();
    const groups = useNavGroups();
    const bestActivePath = useBestActivePath(groups, pathname);
    const [collapsed, setCollapsed] = useState(defaultCollapsed);
    const [expandedIds, setExpandedIds] = useState(() => new Set());
    // Auto-expand whichever branch contains the active route, once groups/route are known --
    // doesn't stomp on branches the user has since toggled open/closed themselves.
    useEffect(() => {
        for (const g of groups) {
            const ancestors = findAncestorIds(g.nodes, bestActivePath);
            if (ancestors && ancestors.length > 0) {
                setExpandedIds((prev) => {
                    const missing = ancestors.filter((id) => !prev.has(id));
                    if (missing.length === 0)
                        return prev;
                    const next = new Set(prev);
                    missing.forEach((id) => next.add(id));
                    return next;
                });
                break;
            }
        }
    }, [groups, bestActivePath]);
    const toggle = (id) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    };
    return (_jsxs(Box, { sx: { display: "flex", minHeight: "70vh" }, children: [_jsxs(Box, { sx: {
                    width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH,
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    borderRight: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    overflowY: "auto",
                    overflowX: "hidden",
                    transition: "width 120ms ease",
                    px: 0.75,
                    py: 1,
                }, children: [_jsx(Box, { sx: { flex: 1 }, children: groups.map((group, i) => (_jsxs(Box, { sx: { mb: 0.5 }, children: [group.section && !collapsed && (_jsx(Typography, { sx: { px: 1.25, pt: i === 0 ? 0.25 : 1, pb: 0.5, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.secondary" }, children: group.section })), group.section && collapsed && _jsx(Box, { sx: { height: "1px", mx: 0.75, my: 1, bgcolor: "divider" } }), group.nodes.map((node) => (_jsx(NavRow, { entry: node, depth: 0, collapsed: collapsed, bestActivePath: bestActivePath, expandedIds: expandedIds, onToggle: toggle }, node.id)))] }, group.section ?? `g${i}`))) }), _jsx(Box, { sx: { display: "flex", justifyContent: "center", pt: 0.75, borderTop: "1px solid", borderColor: "divider" }, children: _jsx(Tooltip, { title: collapsed ? "Expand menu" : "Collapse menu", placement: "right", children: _jsx(IconButton, { size: "small", onClick: () => setCollapsed((c) => !c), sx: { border: "1px solid", borderColor: "divider", borderRadius: 1, width: 28, height: 28 }, children: _jsx(PanelLeftOutlinedIcon, { sx: { fontSize: 15, transform: collapsed ? "rotate(180deg)" : "none" } }) }) }) })] }), _jsx(Box, { component: "main", sx: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }, children: children })] }));
}
